import React, { useEffect, useState } from "react";
import { X, Save, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, cachedGet, formatApiError } from "../lib/api";

// Lead fields an admin can mark mandatory for a stage transition.
// Keep in sync with /app/backend/business_rules.py FIELD_LABELS.
const FIELD_OPTIONS = [
  { key: "lead_source", label: "Lead Source" },
  { key: "sales_poc", label: "Pipeline" },
  { key: "company_name", label: "Company Name" },
  { key: "website_url", label: "Website URL" },
  { key: "client_poc_name", label: "Client POC Name" },
  { key: "poc_designation", label: "POC Designation" },
  { key: "category", label: "Category" },
  { key: "remarks", label: "Remarks" },
  { key: "opportunity_type", label: "Opportunity Type" },
  { key: "warehouse_location", label: "Warehouse Location" },
  { key: "hq_city", label: "HQ City" },
  { key: "hq_state", label: "HQ State" },
  { key: "current_wh_partner", label: "Current WH Partner" },
  { key: "current_lm_partner", label: "Current LM Partner" },
  { key: "wms_oms", label: "WMS / OMS" },
  { key: "monthly_order_potential", label: "Monthly Order Potential" },
  { key: "overall_monthly_revenue", label: "Overall Monthly Revenue" },
  { key: "universal_order_potential", label: "Universal Order Potential" },
  { key: "mg_amount", label: "MG Amount" },
  { key: "interested_month", label: "Interested Month" },
  { key: "tentative_live_month", label: "Tentative Live Month" },
  { key: "agreement_initiated_month", label: "Agreement Initiated Month" },
  { key: "oib_month", label: "OIB Month" },
  { key: "live_month", label: "Live Month" },
  { key: "lost_reason", label: "Lost / Not Int Reason" },
];

const PICKUP_RULES = [
  { type: "none", label: "Never auto-pickable" },
  { type: "working_days", label: "After N working days of no activity" },
  { type: "calendar_days", label: "After N calendar days of no activity" },
  { type: "cold_or_lost_only", label: "Only when tag is Cold or Lost" },
  { type: "calendar_days_cold_or_lost", label: "After N calendar days + Cold/Lost tag" },
];

export default function StageConfigDialog({ open, stage, onClose, onSaved }) {
  const [taggingRequired, setTaggingRequired] = useState(false);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [pickupType, setPickupType] = useState("none");
  const [pickupValue, setPickupValue] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !stage) return;
    const extra = stage.extra || {};
    setTaggingRequired(!!extra.tagging_required);
    setMandatoryFields(Array.isArray(extra.mandatory_fields) ? extra.mandatory_fields : []);
    const rule = extra.pickup_rule || { type: "none" };
    setPickupType(rule.type || "none");
    setPickupValue(typeof rule.value === "number" ? rule.value : 0);
  }, [open, stage]);

  if (!open || !stage) return null;

  const toggleField = (key) => {
    setMandatoryFields((prev) => (prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]));
  };

  const save = async () => {
    setSaving(true);
    const pickup_rule = ["working_days", "calendar_days", "calendar_days_cold_or_lost"].includes(pickupType)
      ? { type: pickupType, value: Number(pickupValue) || 0 }
      : { type: pickupType };
    const extra = {
      tagging_required: !!taggingRequired,
      mandatory_fields: mandatoryFields,
      pickup_rule,
    };
    try {
      await api.patch(`/masters/${stage.id}`, { extra });
      cachedGet.invalidate((k) => k.startsWith("/masters"));
      toast.success(`Updated config for "${stage.value}"`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const needsNumeric = ["working_days", "calendar_days", "calendar_days_cold_or_lost"].includes(pickupType);

  return (
    <div
      data-testid="stage-config-dialog"
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !saving && onClose()}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
            <Settings2 size={16} className="text-orange-600" /> Configure stage — {stage.value}
          </h3>
          <button onClick={() => !saving && onClose()} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Tagging required */}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="stage-config-tagging-toggle">
            <input
              type="checkbox"
              checked={taggingRequired}
              onChange={(e) => setTaggingRequired(e.target.checked)}
              className="accent-orange-500"
            />
            <span className="text-sm font-medium text-slate-800">Require a tag (Hot / Warm / Cold / Lost) when entering this stage</span>
          </label>

          {/* Pickup rule */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Auto-pickup rule</label>
            <div className="flex flex-wrap gap-2 items-center">
              <select
                data-testid="stage-config-pickup-type"
                value={pickupType}
                onChange={(e) => setPickupType(e.target.value)}
                className="text-sm px-3 py-2 border border-slate-200 rounded-md bg-white flex-1 min-w-[260px]"
              >
                {PICKUP_RULES.map((r) => <option key={r.type} value={r.type}>{r.label}</option>)}
              </select>
              {needsNumeric && (
                <input
                  data-testid="stage-config-pickup-value"
                  type="number"
                  min="0"
                  value={pickupValue}
                  onChange={(e) => setPickupValue(e.target.value)}
                  className="text-sm px-3 py-2 border border-slate-200 rounded-md w-24"
                  placeholder="N"
                />
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Leads sit unowned after the rule triggers — anyone with permission can &quot;pick&quot; them.
            </p>
          </div>

          {/* Mandatory fields */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Mandatory fields ({mandatoryFields.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="stage-config-clear-all"
                  onClick={() => setMandatoryFields([])}
                  className="text-[11px] text-orange-600 hover:underline"
                >
                  Clear all
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Lead must have all of these populated before it can be moved INTO {stage.value}. Leave empty for no checks.
            </p>
            <div className="border border-slate-200 rounded-lg p-3 max-h-64 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1.5 bg-slate-50/40">
              {FIELD_OPTIONS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-sm">
                  <input
                    data-testid={`stage-config-field-${f.key}`}
                    type="checkbox"
                    checked={mandatoryFields.includes(f.key)}
                    onChange={() => toggleField(f.key)}
                    className="accent-orange-500"
                  />
                  <span className="text-slate-700">{f.label}</span>
                  <span className="text-[10px] text-slate-400">({f.key})</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="stage-config-save"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Save size={13} /> {saving ? "Saving…" : "Save config"}
          </button>
        </div>
      </div>
    </div>
  );
}
